import { Controller } from "@hotwired/stimulus"
import Cropper from "cropperjs"
import "cropperjs/dist/cropper.css"

type OnCroppedCallback = (croppedFile: File) => void
type OnCancelCallback = () => void

interface RequestCropDetail {
  file: File
  onCropped?: OnCroppedCallback
  onCancel?: OnCancelCallback
}

interface DropzoneNeedsCropDetail {
  file: File
  dropzone: { addFile: (f: File) => void; removeFile: (f: File) => void }
}

export default class extends Controller {
  static targets = ["modal", "modalBox", "image"]
  static values = {
    maxWidth: { type: Number, default: 512 },
    maxHeight: { type: Number, default: 512 },
    aspectRatio: { type: Number, default: 1 }
  }

  declare readonly modalTarget: HTMLElement
  declare readonly modalBoxTarget: HTMLElement
  declare readonly imageTarget: HTMLImageElement
  declare readonly maxWidthValue: number
  declare readonly maxHeightValue: number
  declare readonly aspectRatioValue: number

  private cropper?: Cropper
  private currentFile?: File
  private cropperReady: boolean = false
  private onCropped?: OnCroppedCallback
  private onCancel?: OnCancelCallback

  private boundHandleRequestCrop = this.handleRequestCrop.bind(this) as EventListener
  private boundHandleDropzoneNeedsCrop = this.handleDropzoneNeedsCrop.bind(this) as EventListener

  connect() {
    document.addEventListener("image-cropper:request-crop", this.boundHandleRequestCrop)
    document.addEventListener("dropzone:image-needs-crop", this.boundHandleDropzoneNeedsCrop)
  }

  disconnect() {
    document.removeEventListener("image-cropper:request-crop", this.boundHandleRequestCrop)
    document.removeEventListener("dropzone:image-needs-crop", this.boundHandleDropzoneNeedsCrop)
    this.destroyCropper()
  }

  private handleRequestCrop(event: Event) {
    const detail = (event as CustomEvent<RequestCropDetail>).detail
    if (!detail?.file) return

    this.openCropper(detail.file, detail.onCropped, detail.onCancel)
  }

  private handleDropzoneNeedsCrop(event: Event) {
    const detail = (event as CustomEvent<DropzoneNeedsCropDetail>).detail
    if (!detail?.file || !detail?.dropzone) return

    const { file, dropzone } = detail

    this.openCropper(
      file,
      (croppedFile) => {
        dropzone.removeFile(file)
        // Defer to ensure modal is fully closed before re-adding
        setTimeout(() => dropzone.addFile(croppedFile), 100)
      },
      () => {
        dropzone.removeFile(file)
      }
    )
  }

  private openCropper(file: File, onCropped?: OnCroppedCallback, onCancel?: OnCancelCallback) {
    this.currentFile = file
    this.onCropped = onCropped
    this.onCancel = onCancel

    const reader = new FileReader()
    reader.onload = (e) => {
      if (e.target?.result) {
        this.imageTarget.src = e.target.result as string
        this.imageTarget.onload = () => {
          this.initializeCropper()
          this.openModal()
        }
      }
    }
    reader.readAsDataURL(file)
  }

  private initializeCropper() {
    this.destroyCropper()
    this.cropperReady = false

    this.imageTarget.style.display = "block"

    this.cropper = new Cropper(this.imageTarget, {
      aspectRatio: this.aspectRatioValue,
      viewMode: 1,
      dragMode: "move",
      autoCropArea: 0.8,
      restore: false,
      guides: true,
      center: true,
      highlight: false,
      cropBoxMovable: true,
      cropBoxResizable: true,
      zoomable: true,
      zoomOnTouch: true,
      zoomOnWheel: true,
      ready: () => {
        this.cropperReady = true
      }
    })
  }

  crop() {
    if (!this.cropper || !this.currentFile || !this.cropperReady) return

    const originalFile = this.currentFile
    const onCropped = this.onCropped

    try {
      const canvas = this.cropper.getCroppedCanvas({
        maxWidth: this.maxWidthValue,
        maxHeight: this.maxHeightValue,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: "high"
      })

      if (!canvas) {
        this.cancel()
        return
      }

      this.processCroppedCanvas(canvas, originalFile, onCropped)
    } catch (error) {
      console.error("Error cropping image:", error)
      this.cancel()
    }
  }

  private processCroppedCanvas(
    canvas: HTMLCanvasElement,
    originalFile: File,
    onCropped?: OnCroppedCallback
  ) {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          this.cancel()
          return
        }

        const croppedFile = new File([blob], originalFile.name, {
          type: originalFile.type || "image/png",
          lastModified: Date.now()
        })

        // Mark the cropped file so dropzone won't re-trigger cropping for it
        ;(croppedFile as File & { alreadyCropped?: boolean }).alreadyCropped = true

        this.closeModal()
        this.clearState()

        if (onCropped) onCropped(croppedFile)
      },
      originalFile.type || "image/png",
      0.9
    )
  }

  cancel() {
    const onCancel = this.onCancel
    this.closeModal()
    this.clearState()
    if (onCancel) onCancel()
  }

  private clearState() {
    this.currentFile = undefined
    this.onCropped = undefined
    this.onCancel = undefined
  }

  private openModal() {
    if (this.modalTarget instanceof HTMLDialogElement) {
      this.modalTarget.showModal()
    } else {
      this.modalTarget.classList.add("modal-open")
    }
  }

  private closeModal() {
    if (this.modalTarget instanceof HTMLDialogElement) {
      this.modalTarget.close()
    } else {
      this.modalTarget.classList.remove("modal-open")
    }
    this.destroyCropper()

    this.imageTarget.style.display = "none"
    this.imageTarget.src = ""
  }

  private destroyCropper() {
    if (this.cropper) {
      this.cropper.destroy()
      this.cropper = undefined
    }
  }
}
